import React, { useEffect, useState, Suspense, useRef, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  Alert,
  Snackbar,
  Paper,
  Toolbar,
  AppBar,
  IconButton,
  Button,
  useMediaQuery,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import StraightenOutlinedIcon from '@mui/icons-material/StraightenOutlined';
import DocumentScannerOutlinedIcon from '@mui/icons-material/DocumentScannerOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import EditIcon from '@mui/icons-material/Edit';
import { ArrowBack, ChevronRight, Close, DescriptionOutlined, PhotoLibraryOutlined, PictureAsPdf, Warning as WarningIcon, AssignmentOutlined, Compare } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useProject } from '../hooks/useProject';
import { Loading } from '../components/Loading';
import PdfViewer from '../components/PdfViewer';
import theme from '../theme';
import { WithDeviceProps } from '../hooks/withDevice';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { LoadingFullScreen } from '../components/LoadingFullScreen';
import { defaultRetryPolicy } from '../utils/retryPolicy';
import { useZenitalCamera } from '../hooks/useZenitalCamera';
import { captureException } from '../utils/captureException';

// Lazy load heavy dialog components to improve initial load performance
// Store import functions for preloading
const identificationImport = () => import('../components/Identification');
const captureImport = () => import('../components/Capture');
const optionsImport = () => import('../components/Options');
const measurementsImport = () => import('../components/Measurements');
const seriesImport = () => import('../components/Series');
const finishImport = () => import('../components/Finish');

const Identification = React.lazy(identificationImport);
const Capture = React.lazy(captureImport);
const Options = React.lazy(optionsImport);
const Measurements = React.lazy(measurementsImport);
const Series = React.lazy(seriesImport);
const Finish = React.lazy(finishImport);


const ProjectDetails: React.FC<WithDeviceProps> = ({ isMobileDevice }) => {
  const { t } = useTranslation();
  
  // Define the inspection steps list
  const inspectionSteps = [
    {
      id: 'vehicle-identification',
      icon: LocalShippingOutlinedIcon,
      text: t('steps.vehicleIdentification'),
    },
    {
      id: 'image-capture',
      icon: CameraAltOutlinedIcon,
      text: t('steps.imageCapture'),
    },
    {
      id: 'vehicle-options',
      icon: BuildOutlinedIcon,
      text: t('steps.vehicleOptions'),
    },
    {
      id: 'measurements',
      icon: StraightenOutlinedIcon,
      text: t('steps.measurements'),
    },
    {
      id: 'series-comparison',
      icon: DocumentScannerOutlinedIcon,
      text: t('steps.seriesComparison'),
    },
    {
      id: 'finalize-inspection',
      icon: CheckCircleOutlineOutlinedIcon,
      text: t('steps.finalizeInspection'),
    },
  ];
  const { projectCode } = useParams<{ projectCode: string }>();
  const [openIdentification, setOpenIdentification] = useState(false);
  const [openCapture, setOpenCapture] = useState(false);
  const [openOptions, setOpenOptions] = useState(false);
  const [openMeasurements, setOpenMeasurements] = useState(false);
  const [openSeries, setOpenSeries] = useState(false);
  const [openFinish, setOpenFinish] = useState(false);
  const [openPdf, setOpenPdf] = useState(false);
  const [pdfType, setPdfType] = useState<string | undefined>(undefined);
  const { project, loading, error, refetch, setProject } = useProject({ project_code: projectCode || '' });
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [waitingForPdf, setWaitingForPdf] = useState(true);
  const { user } = useAuth();
  const [errorSavingStatus, setErrorSavingStatus] = useState<string | null>(null);
  const [prevOpenStates, setPrevOpenStates] = useState<{ [key: string]: boolean }[]>([{
    identification: false,
    capture: false,
    options: false,
    measurements: false,
    series: false,
    finish: false,
    pdf: false,
  }]);
  const [loadingComponent, setLoadingComponent] = useState<string | null>(null);
  const preloadedComponents = useRef<Set<string>>(new Set());
  const hasDialogOpenRef = useRef(false);
  const historyStatePushedRef = useRef(false);
  const isHandlingPopStateRef = useRef(false);
  const isCleaningUpHistoryRef = useRef(false);
  const [openEditableDialog, setOpenEditableDialog] = useState(false);
  const [savingEditable, setSavingEditable] = useState(false);
  const [editableError, setEditableError] = useState<string | null>(null);
  const { zenitalCamera, loading: zenitalCameraLoading, error: zenitalCameraError } = useZenitalCamera();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    setErrorMessage(zenitalCameraError);
  }, [zenitalCameraError]);
  
  // Helper function to check if any dialog is open
  const hasAnyDialogOpen = () => {
    return openIdentification || openCapture || openOptions || openMeasurements || openSeries || openFinish || openPdf;
  };

  const handleCloseAll = useCallback(() => {
    setPrevOpenStates([]);
    setOpenIdentification(false);
    setOpenCapture(false);
    setOpenOptions(false);
    setOpenMeasurements(false);
    setOpenSeries(false);
    setOpenFinish(false);
    setOpenPdf(false);
    setLoadingComponent(null);
  }, []);

  useEffect(() => {
    if (!loading) {
      setTimeout(() => {
        setWaitingForPdf(false);
      }, 1000);
    }
  }, [loading]);

  // Handle browser back button when dialogs are open
  useEffect(() => {
    const hasDialog = hasAnyDialogOpen();
    const previousHasDialog = hasDialogOpenRef.current;
    hasDialogOpenRef.current = hasDialog;

    // Push a history state when a dialog opens (transition from no dialog to dialog)
    if (hasDialog && !previousHasDialog) {
      window.history.pushState({ dialogOpen: true }, '');
      historyStatePushedRef.current = true;
    }

    // Clean up history state when dialogs close normally (not via back button)
    if (!hasDialog && previousHasDialog && !isHandlingPopStateRef.current && historyStatePushedRef.current) {
      // Set flag to indicate we're cleaning up, so popstate handler can ignore it
      isCleaningUpHistoryRef.current = true;
      // Remove the history entry we added when the dialog opened
      window.history.go(-1);
      historyStatePushedRef.current = false;
      // Reset the flag after cleanup
      setTimeout(() => {
        isCleaningUpHistoryRef.current = false;
      }, 0);
    }

    // Handle popstate event (browser/smartphone back button)
    const handlePopState = (event: PopStateEvent) => {
      // If we're cleaning up history, ignore this popstate event
      if (isCleaningUpHistoryRef.current) {
        return;
      }

      isHandlingPopStateRef.current = true;
      const currentHasDialog = hasAnyDialogOpen();
      if (currentHasDialog) {
        // Prevent navigation and close all dialogs
        handleCloseAll();
        // Push state to prevent navigation (browser already navigated back, so we push forward)
        // window.history.pushState({ dialogOpen: true }, '');
        historyStatePushedRef.current = true;
      } else {
        // Dialog was closed via back button, reset the flag
        historyStatePushedRef.current = false;
      }
      // Reset the flag after a short delay to allow state updates to complete
      setTimeout(() => {
        isHandlingPopStateRef.current = false;
      }, 0);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [openIdentification, openCapture, openOptions, openMeasurements, openSeries, openFinish, openPdf, handleCloseAll]);

  // Preload components when project is loaded to reduce loading delay
  useEffect(() => {
    if (project && !loading) {
      // Preload all components in the background
      const componentsToPreload = [
        { key: 'identification', importFn: identificationImport },
        { key: 'capture', importFn: captureImport },
        { key: 'options', importFn: optionsImport },
        { key: 'measurements', importFn: measurementsImport },
        { key: 'series', importFn: seriesImport },
        { key: 'finish', importFn: finishImport },
      ];

      componentsToPreload.forEach(({ key, importFn }) => {
        if (!preloadedComponents.current.has(key)) {
          preloadedComponents.current.add(key);
          // Start loading the component
          importFn();
        }
      });
    }
  }, [project, loading]);

  // Clear loading state when dialogs open (Suspense will handle loading from here)
  useEffect(() => {
    if (openIdentification || openCapture || openOptions || openMeasurements || openSeries || openFinish) {
      setTimeout(() => {
        setLoadingComponent(null);
      }, 400);
    }
  }, [openIdentification, openCapture, openOptions, openMeasurements, openSeries, openFinish]);

  const handleClose = () => {
    setLoadingComponent("back-button");
    const lastOpenState = prevOpenStates.pop();
    if (lastOpenState) {
      setOpenIdentification(lastOpenState.identification);
      setOpenCapture(lastOpenState.capture);
      setOpenOptions(lastOpenState.options);
      setOpenMeasurements(lastOpenState.measurements);
      setOpenSeries(lastOpenState.series);
      setOpenFinish(lastOpenState.finish);
      setOpenPdf(lastOpenState.pdf);
    }
    setTimeout(() => {
      setLoadingComponent(null);
    }, 400);
  };

  const handleClosePdf = () => {
    setOpenPdf(false);
  };

  const saveEditable = async () => {
    setSavingEditable(true);
    setEditableError(null);
    try {
      const idToken = await user?.getIdToken();
      if (!idToken) {
        throw new Error('No idToken found');
      }
      const apiUrl = `${process.env.REACT_APP_API_URL || ''}/api/project/${project?.project_code}`;
      await defaultRetryPolicy.executeWithRetry(async () => {
        return await axios.put(apiUrl, {
          editable: true,
          status: 'progress'
        }, {
          headers: { 'Authorization': `Bearer ${idToken}` },
        });
      }, 'Save comments API call', {
        apiUrl,
        projectCode: project?.project_code,
      });
      await refetch();
      setOpenEditableDialog(false);
    } catch (error: any) {
      captureException(error, { tags: { feature: 'projectDetails' }, extra: { action: 'handleSaveComments', project_code: project?.project_code } });
      console.error('Error saving comments:', error);
      setEditableError(error?.response?.data?.error || error?.message || 'Error saving comments');
    } finally {
      setSavingEditable(false);
    }
  };

  const handleOpen = (id: string) => {
    // Set loading state and preload component if needed
    setLoadingComponent(id);

    setPrevOpenStates(
      [{
        identification: false,
        capture: false,
        options: false,
        measurements: false,
        series: false,
        finish: false,
        pdf: false,
      }]
    );
    
    // Preload the component if not already preloaded
    if (id === 'vehicle-identification' && !preloadedComponents.current.has('identification')) {
      preloadedComponents.current.add('identification');
      identificationImport();
    }
    if (id === 'image-capture' && !preloadedComponents.current.has('capture')) {
      preloadedComponents.current.add('capture');
      captureImport();
    }
    if (id === 'vehicle-options' && !preloadedComponents.current.has('options')) {
      preloadedComponents.current.add('options');
      optionsImport();
    }
    if (id === 'measurements' && !preloadedComponents.current.has('measurements')) {
      preloadedComponents.current.add('measurements');
      measurementsImport();
    }
    if (id === 'series-comparison' && !preloadedComponents.current.has('series')) {
      preloadedComponents.current.add('series');
      seriesImport();
    }
    if (id === 'finalize-inspection' && !preloadedComponents.current.has('finish')) {
      preloadedComponents.current.add('finish');
      finishImport();
    }
    
    // Open the dialog immediately - loading state will show fallback until Suspense takes over
    if (id === 'vehicle-identification') {
      setOpenIdentification(true);
    }
    if (id === 'image-capture') {
      setOpenCapture(true);
    }
    if (id === 'vehicle-options') {
      setOpenOptions(true);
    }
    if (id === 'measurements') {
      setOpenMeasurements(true);
    }
    if (id === 'series-comparison') {
      setOpenSeries(true);
    }
    if (id === 'finalize-inspection') {
      setOpenFinish(true);
    }
  };

  const handleNextStep = async (id: string) => {
    setPrevOpenStates([...prevOpenStates, {
      identification: openIdentification,
      capture: openCapture,
      options: openOptions,
      measurements: openMeasurements,
      series: openSeries,
      finish: openFinish,
    }]);
    
    // Set loading state for the next component to open
    if (id === 'init') {
      setLoadingComponent('vehicle-identification');
      setOpenIdentification(true);
    }
    if (id === 'vehicle-identification') {
      if (
        project &&
        project.ficha_rosroca?.status === "completed" &&
        project.xapa_rosroca?.status === "completed" &&
        project.chasis_rosroca?.status === "completed" &&
        project.status_inspeccion !== "completed"
      ){
        const idToken = await user?.getIdToken();
        if (!idToken) {
          throw new Error('No idToken found');
        }
        axios.put(`${process.env.REACT_APP_API_URL || ''}/api/project/${project.project_code}`, {
          status_inspeccion: "completed",
        }, {
          headers: {
            'Authorization': `Bearer ${idToken}`,
          },
        }).then(() => {
          refetch();
          setErrorSavingStatus(null);
        }).catch((error) => {
          captureException(error, { tags: { feature: 'projectDetails' }, extra: { action: 'handleNextStep', project_code: project?.project_code } });
          setErrorSavingStatus(error.response.data.error || t('errors.project.savingStatusFailed'));
        });
      }
      setLoadingComponent('image-capture');
      setOpenCapture(true);
      setOpenIdentification(false);
    }
    if (id === 'image-capture') {
      setLoadingComponent('vehicle-options');
      setOpenOptions(true);
      setOpenCapture(false);
    }
    if (id === 'vehicle-options') {
      setLoadingComponent('measurements');
      setOpenMeasurements(true);
      setOpenOptions(false);
    }
    if (id === 'measurements') {
      setLoadingComponent('series-comparison');
      setOpenSeries(true);
      setOpenMeasurements(false);
    }
    if (id === 'series-comparison') {
      setLoadingComponent('finalize-inspection');
      setOpenFinish(true);
      setOpenSeries(false);
    }
    if (id === 'finalize-inspection') {
      setOpenFinish(false);
      setLoadingComponent(null);
    }
  };

  return (
      <>
      {projectCode && (
        <Box sx={{ height: "calc(100vh - 56px - " + (isSmallScreen ? "56px" : "64px") + ")" }}>
          {!hasAnyDialogOpen() && loadingComponent === null && (<AppBar
            position="static"
            color="inherit"
            // sx={{ bgcolor: 'red' }}
          >
            <Toolbar sx={{ p: 1 }}>
              <IconButton onClick={() => navigate(-1)} color="inherit" sx={{ mr: 1 }}><ArrowBack/></IconButton>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>{projectCode}</Typography>
              {project?.warning_code_missing && (
                <Tooltip title="Codigo de proyecto no encontrado en informe de lanzamiento" placement="bottom">
                  <span>
                    <IconButton sx={{ 
                      mr: 0.5,
                      cursor: 'default',
                      '&.Mui-disabled': {
                        opacity: 1,
                        color: 'warning.main',
                        '& .MuiIconButton-root': {
                          opacity: 1,
                          color: 'warning.main',
                        },
                        '& .MuiSvgIcon-root': {
                          opacity: 1,
                        },
                      },
                        }} disabled>
                      <WarningIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              
              {project?.gcs_series_book_path && (
                <Tooltip title={t('projectList.seriesBookName', 'Libro de Series')} placement="bottom">
                  <IconButton sx={{ mr: 0.5 }} onClick={() => {
                    setOpenPdf(true); 
                    setPdfType('series');}} disabled={loading || waitingForPdf}><Compare /></IconButton>
                </Tooltip>
              )}
              {project?.gcs_homologation_book_path && (
                <Tooltip title={t('projectList.homologationBookName', 'Libro de Homologación')} placement="bottom">
                  <IconButton sx={{ mr: 0.5 }} onClick={() => {
                    setOpenPdf(true); 
                    setPdfType('homologation');}} disabled={loading || waitingForPdf}><AssignmentOutlined /></IconButton>
                </Tooltip>
              )}
              {project?.gcs_image_book_path && (
                <Tooltip title={t('projectList.imageBookName', 'Libro de Fotos')} placement="bottom">
                  <IconButton sx={{ mr: 0.5 }} onClick={() => {
                    setOpenPdf(true); 
                    setPdfType('image_book');}} disabled={loading || waitingForPdf}><PhotoLibraryOutlined /></IconButton>
                </Tooltip>
              )}
              {project?.gcs_report_path && (
                <Tooltip title={t('projectList.reportName', 'Reporte de Opcionales')} placement="bottom">
                  <IconButton sx={{ mr: 0.5 }} onClick={() => {
                    setOpenPdf(true); 
                    setPdfType('report');}} disabled={loading || waitingForPdf}><DescriptionOutlined /></IconButton>
                </Tooltip>
              )}
              {project?.gcs_pdf_path && (
                <Tooltip title={t('projectList.launchName', 'Lanzamiento')} placement="bottom">
                  <IconButton onClick={() => {
                    setOpenPdf(true); 
                    setPdfType('lanzamiento');}} disabled={loading || waitingForPdf}><PictureAsPdf /></IconButton>
                </Tooltip>
              )}
              {(project?.editable === false) && (
                <Tooltip title={t('projectList.editable', 'Editar Projecto')} placement="bottom">
                  <IconButton 
                    sx={{ mr: 0.5 }} 
                    onClick={() => setOpenEditableDialog(true)}
                    disabled={loading || savingEditable}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Toolbar>
          </AppBar>
          )}
          {loading && (
            <Loading />
          )}
          {project && (
            <Container component="section" maxWidth="lg" sx={{ flexGrow: 1, py: 3, pl: 2, pr: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {inspectionSteps.map((step) => {
                  const IconComponent = step.icon;
                  return (
                    <Paper 
                      key={step.id}
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between', 
                        p: 2, 
                        borderRadius: 2,
                        cursor: 'pointer',
                        // Prevent text selection on long press
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        MozUserSelect: 'none',
                        msUserSelect: 'none',

                        // Disable iOS callout menu (copy/share)
                        WebkitTouchCallout: 'none',

                        // Remove mobile tap highlight
                        WebkitTapHighlightColor: 'transparent',

                        '&:hover': {
                          backgroundColor: 'action.selected',
                        },
                      }}
                      onClick={() => {
                        handleOpen(step.id);
                      }}
                    >
                      <Box sx={{ p: 1, display: 'flex', alignItems: 'center' }}>
                        <IconComponent sx={{ color: 'primary.main' }} />
                      </Box>
                      <Typography 
                        variant="h6" 
                        gutterBottom 
                        sx={{ 
                          width: "100%", 
                          pl: 1, 
                          fontSize: 16, 
                          mb: 0, 
                          textAlign: 'left' 
                        }}
                      >
                        {step.text}
                      </Typography>
                      <ChevronRight sx={{ color: 'text.secondary' }} />
                    </Paper>
                  );
                })}
              </Box>
              </Container>
            )}
          </Box>
        )}

      {/* Error Snackbar */}
      <Snackbar
        open={!!error && project !== null}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" sx={{ width: '100%', borderRadius: '0px !important' }}>
          {error}
        </Alert>
      </Snackbar>
      {error && project === null && (
        <Alert 
          severity="error" 
          sx={{ 
            width: '100%', 
            borderRadius: '0px !important',
            maxWidth: 'none',
            '& .MuiAlert-message': {
              width: '100%'
            }
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
            {error}
            <Button 
              sx={{ width: '100%' }}
              onClick={() => {
              navigate(-1);
            }}>
{t('common.back')}
            </Button>
          </Box>
        </Alert>
      )}
      {loadingComponent !== null && (
        <LoadingFullScreen isSmallScreen={isSmallScreen || isMobileDevice} series={project?.gcs_series_book_path} homologation={project?.gcs_homologation_book_path} image={project?.gcs_image_book_path} report={project?.gcs_report_path} lanzamiento={project?.gcs_pdf_path} />
      )}
      {(project && openIdentification) || (project && loadingComponent === 'vehicle-identification') ? (
        <>
          {openIdentification && (
            <Suspense fallback={<LoadingFullScreen isSmallScreen={isSmallScreen || isMobileDevice} series={project?.gcs_series_book_path} homologation={project?.gcs_homologation_book_path} image={project?.gcs_image_book_path} report={project?.gcs_report_path} lanzamiento={project?.gcs_pdf_path} />}>
              <Identification 
                project={project} 
                openIdentification={openIdentification} 
                setProject={setProject} 
                handleClose={handleClose} 
                handleNextStep={handleNextStep} 
                refetch={refetch} 
                onOpenPdf={() => setOpenPdf(true)}
                isSmallScreen={isSmallScreen || isMobileDevice}
                setPdfType={setPdfType}
                handleCloseAll={handleCloseAll}
              />
            </Suspense>
          )}
        </>
      ) : null}
      {(project && openCapture) || (project && loadingComponent === 'image-capture') ? (
        <>
          {loadingComponent === 'image-capture' && (
            <LoadingFullScreen isSmallScreen={isSmallScreen || isMobileDevice} series={project?.gcs_series_book_path} homologation={project?.gcs_homologation_book_path} image={project?.gcs_image_book_path} report={project?.gcs_report_path} lanzamiento={project?.gcs_pdf_path} />
          )}
          {openCapture && (
            <Suspense fallback={<LoadingFullScreen isSmallScreen={isSmallScreen || isMobileDevice} series={project?.gcs_series_book_path} homologation={project?.gcs_homologation_book_path} image={project?.gcs_image_book_path} report={project?.gcs_report_path} lanzamiento={project?.gcs_pdf_path} />}>
              <Capture 
                project={project} 
                openCapture={openCapture} 
                handleClose={handleClose} 
                handleNextStep={handleNextStep} 
                refetch={refetch} 
                onOpenPdf={() => setOpenPdf(true)}
                isSmallScreen={isSmallScreen || isMobileDevice}
                setProject={setProject}
                setPdfType={setPdfType}
                handleCloseAll={handleCloseAll}
                zenitalCamera={zenitalCamera || undefined}
              />
            </Suspense>
          )}
        </>
      ) : null}
      {(project && openOptions) || (project && loadingComponent === 'vehicle-options') ? (
        <>
          {loadingComponent === 'vehicle-options' && (
            <LoadingFullScreen isSmallScreen={isSmallScreen || isMobileDevice} series={project?.gcs_series_book_path} homologation={project?.gcs_homologation_book_path} image={project?.gcs_image_book_path} report={project?.gcs_report_path} lanzamiento={project?.gcs_pdf_path} />
          )}
          {openOptions && (
            <Suspense fallback={<LoadingFullScreen isSmallScreen={isSmallScreen || isMobileDevice} series={project?.gcs_series_book_path} homologation={project?.gcs_homologation_book_path} image={project?.gcs_image_book_path} report={project?.gcs_report_path} lanzamiento={project?.gcs_pdf_path} />}>
              <Options 
                project={project} 
                openOptions={openOptions} 
                handleClose={handleClose} 
                handleNextStep={handleNextStep} 
                setProject={setProject} 
                onOpenPdf={() => setOpenPdf(true)}
                isSmallScreen={isSmallScreen || isMobileDevice}
                setPdfType={setPdfType}
                refetch={refetch}
                handleCloseAll={handleCloseAll}
              />
            </Suspense>
          )}
        </>
      ) : null}
      {(project && openMeasurements) || (project && loadingComponent === 'measurements') ? (
        <>
          {loadingComponent === 'measurements' && (
            <LoadingFullScreen isSmallScreen={isSmallScreen || isMobileDevice} series={project?.gcs_series_book_path} homologation={project?.gcs_homologation_book_path} image={project?.gcs_image_book_path} report={project?.gcs_report_path} lanzamiento={project?.gcs_pdf_path} />
          )}
          {openMeasurements && (
            <Suspense fallback={<LoadingFullScreen isSmallScreen={isSmallScreen || isMobileDevice} series={project?.gcs_series_book_path} homologation={project?.gcs_homologation_book_path} image={project?.gcs_image_book_path} report={project?.gcs_report_path} lanzamiento={project?.gcs_pdf_path} />}>
              <Measurements 
                project={project} 
                openMeasurements={openMeasurements} 
                handleClose={handleClose} 
                handleNextStep={handleNextStep} 
                onOpenPdf={() => setOpenPdf(true)}
                isSmallScreen={isSmallScreen || isMobileDevice}
                setPdfType={setPdfType}
                handleCloseAll={handleCloseAll}
                refetch={refetch}
              />
            </Suspense>
          )}
        </>
      ) : null}
      {(project && openSeries) || (project && loadingComponent === 'series-comparison') ? (
        <>
          {loadingComponent === 'series-comparison' && (
            <LoadingFullScreen isSmallScreen={isSmallScreen || isMobileDevice} series={project?.gcs_series_book_path} homologation={project?.gcs_homologation_book_path} image={project?.gcs_image_book_path} report={project?.gcs_report_path} lanzamiento={project?.gcs_pdf_path} />
          )}
          {openSeries && (
            <Suspense fallback={<LoadingFullScreen isSmallScreen={isSmallScreen || isMobileDevice} series={project?.gcs_series_book_path} homologation={project?.gcs_homologation_book_path} image={project?.gcs_image_book_path} report={project?.gcs_report_path} lanzamiento={project?.gcs_pdf_path} />}>
              <Series 
                project={project} 
                openSeries={openSeries} 
                handleClose={handleClose} 
                handleNextStep={handleNextStep} 
                onOpenPdf={() => setOpenPdf(true)}
                isSmallScreen={isSmallScreen || isMobileDevice}
                setPdfType={setPdfType}
                handleCloseAll={handleCloseAll}
                refetch={refetch}
                setProject={setProject}
              />
            </Suspense>
          )}
        </>
      ) : null}
      {(project && openFinish) || (project && loadingComponent === 'finalize-inspection') ? (
        <>
          {loadingComponent === 'finalize-inspection' && (
            <LoadingFullScreen isSmallScreen={isSmallScreen || isMobileDevice} series={project?.gcs_series_book_path} homologation={project?.gcs_homologation_book_path} image={project?.gcs_image_book_path} report={project?.gcs_report_path} lanzamiento={project?.gcs_pdf_path} />
          )}
          {openFinish && (
            <Suspense fallback={<LoadingFullScreen isSmallScreen={isSmallScreen || isMobileDevice} series={project?.gcs_series_book_path} homologation={project?.gcs_homologation_book_path} image={project?.gcs_image_book_path} report={project?.gcs_report_path} lanzamiento={project?.gcs_pdf_path} />}>
              <Finish 
                project={project} 
                openFinish={openFinish} 
                handleClose={handleClose} 
                handleNextStep={handleNextStep} 
                onOpenPdf={() => setOpenPdf(true)}
                isSmallScreen={isSmallScreen || isMobileDevice}
                refetch={refetch}
                setPdfType={setPdfType}
                handleCloseAll={handleCloseAll}
              />
            </Suspense>
          )}
        </>
      ) : null}
      
      {/* PDF Viewer Dialog */}
      
      <PdfViewer
        open={openPdf}
        onClose={handleClosePdf}
        pdfPath={
          pdfType === 'lanzamiento' ? project?.gcs_pdf_path : 
          pdfType === 'report' ? project?.gcs_report_path : 
          pdfType === 'homologation' ? project?.gcs_homologation_book_path :
          pdfType === 'series' ? project?.gcs_series_book_path :
          project?.gcs_image_book_path
        }
        title={
          pdfType === 'lanzamiento' ? t('projectList.launchTitle', { code: project?.project_code || '' }) : 
          pdfType === 'report' ? t('projectList.reportTitle', { code: project?.project_code || '' }) : 
          pdfType === 'homologation' ? t('projectList.homologationBookTitle', { code: project?.project_code || '' }) :
          pdfType === 'series' ? t('projectList.seriesBookTitle', { code: project?.project_code || '' }) :
          t('projectList.imageBookTitle', { code: project?.project_code || '' })
        }
      />
      {errorSavingStatus && (
        <Alert severity="error" sx={{ borderRadius: '0px !important', fontSize: '0.875rem', zIndex: (theme) => theme.zIndex.drawer + 300, position: 'sticky', bottom: 0, left: 0, right: 0 }}>
          {errorSavingStatus}
          <IconButton onClick={() => setErrorSavingStatus(null)} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ borderRadius: '0px !important', fontSize: '0.875rem', zIndex: (theme) => theme.zIndex.drawer + 300, position: 'sticky', bottom: 0, left: 0, right: 0 }}>
          {errorMessage}
          <IconButton onClick={() => setErrorMessage(null)} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </Alert>
      )}
     <Dialog open={openEditableDialog} onClose={() => !savingEditable && setOpenEditableDialog(false)}>
       <DialogTitle>{t('projectList.enableEditingTitle', 'Habilitar edición')}</DialogTitle>
       <DialogContent>
         <DialogContentText>
           {t('projectList.enableEditingMessage', '¿Quieres habilitar la edición de este proyecto finalizado en modo lectura?')}
         </DialogContentText>
         {editableError && (
           <Alert severity="error" sx={{ mt: 2 }}>
             {editableError}
           </Alert>
         )}
       </DialogContent>
       <DialogActions>
         <Button onClick={() => setOpenEditableDialog(false)} disabled={savingEditable}>
           {t('common.cancel', 'Cancelar')}
         </Button>
         <Button onClick={saveEditable} disabled={savingEditable} variant="contained" color="primary">
           {t('common.confirm', 'Sí, habilitar')}
         </Button>
       </DialogActions>
     </Dialog>
    </>
  );
};

export default ProjectDetails;
